import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, RiskBadge } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function RiskStratification() {
  const [panel, setPanel] = useState([]);
  const [out, setOut] = useState([]);
  const [gaps, setGaps] = useState(null);

  useEffect(() => {
    api.get("/risk-stratification/panel").then(r=>setPanel(r.data));
    api.get("/risk-stratification/outreach-queue").then(r=>setOut(r.data));
    api.get("/risk-stratification/care-gaps").then(r=>setGaps(r.data));
  }, []);

  const counts = {
    high: panel.filter(p=>p.risk==="high").length,
    medium: panel.filter(p=>p.risk==="medium").length,
    low: panel.filter(p=>p.risk==="low").length,
  };

  const panelCols = [
    { key: "patient_name", label: "Patient" },
    { key: "risk", label: "Risk", render: (r)=><RiskBadge risk={r.risk} /> },
    { key: "comorbidity_count", label: "Comorbidities" },
    { key: "ed_visits_12mo", label: "ED visits (12mo)" },
    { key: "social_risk_score", label: "Social risk", render: (r)=>`${(r.social_risk_score*100).toFixed(0)}%` },
  ];

  const outCols = [
    { key: "patient_name", label: "Patient" },
    { key: "reason", label: "Reason" },
    { key: "priority", label: "Priority", render: (r)=><RiskBadge risk={r.priority} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 21" title="Risk Stratification Engine"
        subtitle="Panel-wide risk tiers, proactive outreach queue, care-gap surveillance and population dashboard." testid="risk-page" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Tile label="High Risk" value={counts.high} color="red" />
        <Tile label="Medium Risk" value={counts.medium} color="amber" />
        <Tile label="Low Risk" value={counts.low} color="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <SectionCard title="Patient Panel" testid="panel-section">
            <DataTable data={panel} columns={panelCols} searchKeys={["patient_name","risk"]} testid="panel-table" />
          </SectionCard>
        </div>
        <SectionCard title="Outreach Queue" testid="outreach">
          <DataTable data={out} columns={outCols} searchKeys={["patient_name"]} testid="outreach-table" />
        </SectionCard>
      </div>

      {gaps && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Care Gaps" testid="care-gaps">
            <div className="grid grid-cols-2 gap-3">
              <GapTile label="A1C overdue" value={gaps.a1c_overdue} />
              <GapTile label="Mammogram overdue" value={gaps.mammogram_overdue} />
              <GapTile label="Colonoscopy overdue" value={gaps.colonoscopy_overdue} />
              <GapTile label="Flu vaccine missing" value={gaps.flu_vaccine_missing} />
            </div>
          </SectionCard>
          <SectionCard title="Disease Prevalence" testid="ppd">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={gaps.ppd} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E9E3F5" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#6E6380" }} />
                <YAxis type="category" dataKey="disease" tick={{ fontSize: 11, fill: "#6E6380" }} width={90} />
                <Tooltip />
                <Bar dataKey="n" fill="#9B59B6" radius={[0,6,6,0]} />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>
        </div>
      )}
    </>
  );
}

const Tile = ({ label, value, color }) => (
  <div className="kpi-card">
    <div className="eyebrow">{label}</div>
    <div className={`text-4xl font-semibold mt-2 text-${color}-600`} style={{color: color==="red"?"#dc2626":color==="amber"?"#d97706":"#059669"}}>{value}</div>
  </div>
);

const GapTile = ({ label, value }) => (
  <div className="p-3 bg-brand-bg rounded-md">
    <div className="eyebrow">{label}</div>
    <div className="text-2xl font-semibold text-brand-primary mt-1">{value}</div>
  </div>
);
